import { motion } from 'framer-motion';
import { Zap, Image as ImageIcon, Heart, LayoutGrid, AtSign, Crown, Users } from 'lucide-react';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';

const cellThemes: Record<string, { chip: string; glow: string; text: string }> = {
    lime: { chip: 'bg-lime-400/15 border-lime-400/25 text-lime-300', glow: 'bg-lime-400/10', text: 'text-lime-300' },
    orange: { chip: 'bg-orange-500/15 border-orange-500/25 text-orange-300', glow: 'bg-orange-500/10', text: 'text-orange-300' },
    blue: { chip: 'bg-blue-500/15 border-blue-500/25 text-blue-300', glow: 'bg-blue-500/10', text: 'text-blue-300' },
    pink: { chip: 'bg-pink-500/15 border-pink-500/25 text-pink-300', glow: 'bg-pink-500/10', text: 'text-pink-300' },
    amber: { chip: 'bg-amber-500/15 border-amber-500/25 text-amber-300', glow: 'bg-amber-500/10', text: 'text-amber-300' },
    emerald: { chip: 'bg-emerald-500/15 border-emerald-500/25 text-emerald-300', glow: 'bg-emerald-500/10', text: 'text-emerald-300' },
};

const ShowroomCell = ({ theme = 'lime', icon, label, value, caption, onClick, delay = 0 }: any) => {
    const t = cellThemes[theme] || cellThemes.lime;
    const { impactOccurred } = useHapticFeedback();
    return (
        <motion.button
            whileTap={{ scale: 0.97 }}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay, type: 'spring', stiffness: 260, damping: 26 }}
            onClick={() => { impactOccurred('light'); onClick?.(); }}
            className="relative overflow-hidden group cursor-pointer text-left glass rounded-[20px] p-3 flex flex-col justify-between min-h-[74px] border border-white/10"
        >
            <div className={`pointer-events-none absolute -top-8 -right-8 w-20 h-20 rounded-full blur-2xl transition-opacity opacity-60 group-hover:opacity-100 ${t.glow}`} />
            <div className="relative z-10 flex items-center justify-between w-full gap-1">
                <div className={`w-7 h-7 rounded-[12px] border flex items-center justify-center shadow-lg ${t.chip}`}>
                    {icon}
                </div>
                <span className={`text-[8px] font-black uppercase tracking-wider opacity-70 ${t.text}`}>{label}</span>
            </div>
            <div className="relative z-10">
                <div className={`text-[19px] font-black leading-none tracking-tight ${t.text}`}>{value}</div>
                <div className="text-white/40 text-[9px] font-medium mt-0.5 truncate">{caption}</div>
            </div>
        </motion.button>
    );
};

export const ProfileTiles = ({ user, myCards, mySkins, myBrands, likedCards, onNavigate }: any) => {
    const { impactOccurred } = useHapticFeedback();

    // Admin-only surfaces: the Push Bot (registered-usernames management) card is a
    // staff tool. It must never render for a regular user.
    const isAdminUser = (user as any)?.role === 'admin' || String(user?.telegram_id) === '601287447';

    const go = (route: string, data?: any) => {
        impactOccurred('light');
        onNavigate?.(route, data);
    };

    return (
        <div className="px-4 mb-4">
            <div className="max-w-md mx-auto">
                {/* Section header — real counts from backend */}
                <div className="flex items-center gap-2 mb-3">
                    <span className="text-[10px] font-black uppercase tracking-widest text-white/35">Showroom</span>
                    <div className="flex-1 h-px bg-gradient-to-r from-white/10 to-transparent" />
                    <span className="text-[10px] font-black text-white/40 tabular-nums">
                        {Number(myCards.length || 0) + Number(mySkins.length || 0) + Number(myBrands.length || 0)} items
                    </span>
                </div>

                <div className="grid grid-cols-2 gap-2.5">
                    {/* Collection — real cards count */}
                    <ShowroomCell
                        theme="lime"
                        delay={0}
                        icon={<Zap size={16} fill="currentColor" />}
                        label="Collection"
                        value={myCards.length}
                        caption="Cards owned"
                        onClick={() => go('user_collection', user)}
                    />

                    {/* Covers — real skins count */}
                    <ShowroomCell
                        theme="orange"
                        delay={0.05}
                        icon={<ImageIcon size={16} />}
                        label="Covers"
                        value={mySkins.length}
                        caption="Skins owned"
                        onClick={() => go('user_skins')}
                    />

                    {/* Portfolio — real brands count */}
                    <ShowroomCell
                        theme="blue"
                        delay={0.08}
                        icon={<LayoutGrid size={16} />}
                        label="Portfolio"
                        value={myBrands.length}
                        caption="Brand packages"
                        onClick={() => go('user_collection', user)}
                    />

                    {/* Favorites — real liked count */}
                    <ShowroomCell
                        theme="pink"
                        delay={0.13}
                        icon={<Heart size={16} fill="currentColor" />}
                        label="Favorites"
                        value={likedCards.length}
                        caption="Liked items"
                        onClick={() => go('all_items', 'liked')}
                    />

                    {/* Level — real user level */}
                    <ShowroomCell
                        theme="amber"
                        delay={0.16}
                        icon={<Crown size={16} fill="currentColor" />}
                        label="Level"
                        value={user?.level || 1}
                        caption={user?.next_level_xp ? `${user.current_xp ?? 0}/${user.next_level_xp} XP` : 'Account level'}
                        onClick={() => go('levels')}
                    />

                    {/* Friends — real friend count */}
                    <ShowroomCell
                        theme="emerald"
                        delay={0.2}
                        icon={<Users size={16} />}
                        label="Friends"
                        value={user?.friendCount || 0}
                        caption="Connected"
                        onClick={() => go('inbox')}
                    />

                    {/* Usernames / Push Bot — admin only */}
                    {isAdminUser && (
                    <motion.button
                        whileTap={{ scale: 0.98 }}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.24, type: 'spring', stiffness: 260, damping: 26 }}
                        onClick={() => { impactOccurred('light'); onNavigate?.('usernames'); }}
                        className="col-span-2 glass rounded-[20px] p-4 border border-cyan-500/25 relative overflow-hidden group cursor-pointer text-left min-h-[64px]"
                    >
                        <div className="pointer-events-none absolute -top-10 -right-6 w-28 h-28 bg-cyan-500/10 rounded-full blur-3xl group-hover:opacity-100 opacity-60 transition-opacity" />
                        <div className="relative z-10 flex items-center justify-between gap-3">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 mb-1">
                                    <span className="text-[8px] px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 font-mono font-bold border border-cyan-500/30">Push Bot</span>
                                </div>
                                <h3 className="text-white font-black text-[13px] leading-tight tracking-tight">إدارة يوزراتي المسجلة</h3>
                                <p className="text-white/50 text-[10px] font-medium">عرض، مزاد، وعروض نقل الملكية — بأرقام حقيقية من الخادم</p>
                            </div>
                            <div className="w-8 h-8 rounded-[14px] border border-cyan-500/30 bg-cyan-500/15 text-cyan-300 flex items-center justify-center shadow-lg shrink-0">
                                <AtSign size={15} />
                            </div>
                        </div>
                    </motion.button>
                    )}
                </div>
            </div>
        </div>
    );
};